import {apiFetch, downloadApiBlob} from 'src/api/fetch'

import {
    type ContactWriteInput,
    type CreateCustomerInput,
    type CustomerContact,
    customerContactSchema,
    type CustomerDetail,
    customerDetailSchema,
    type CustomerImportPreview,
    customerImportPreviewSchema,
    type CustomerImportResult,
    customerImportResultSchema,
    type CustomerListFilters,
    type CustomerListItem,
    customerListItemSchema,
    toContactWriteRequest,
    toCreateCustomerRequest,
    toUpdateCustomerRequest,
    type UpdateCustomerInput,
} from './schemas'

function toCustomerListQueryParams(filters: CustomerListFilters = {}): URLSearchParams {
    return new URLSearchParams({query: filters.name?.trim() ?? ''})
}

export async function listCustomers(
    filters: CustomerListFilters = {},
): Promise<CustomerListItem[]> {
    const params = toCustomerListQueryParams(filters)
    const raw = await apiFetch<unknown>(`/customers?${params.toString()}`)
    return customerListItemSchema.array().parse(raw)
}

export async function getCustomer(customerId: string): Promise<CustomerDetail> {
    const raw = await apiFetch<unknown>(`/customers/${customerId}`)
    return customerDetailSchema.parse(raw)
}

export async function createCustomer(input: CreateCustomerInput): Promise<CustomerDetail> {
    const body = toCreateCustomerRequest(input)
    const raw = await apiFetch<unknown>('/customers', {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return customerDetailSchema.parse(raw)
}

export async function updateCustomerPaymentTerms(
    customerId: string,
    paymentTerms: string | null,
): Promise<CustomerDetail> {
    const raw = await apiFetch<unknown>(`/customers/${customerId}/payment-terms`, {
        method: 'PUT',
        body: JSON.stringify({payment_terms: paymentTerms?.trim() || null}),
    })
    return customerDetailSchema.parse(raw)
}

export async function updateCustomer(
    customerId: string,
    input: UpdateCustomerInput,
): Promise<CustomerDetail> {
    const body = toUpdateCustomerRequest(input)
    const raw = await apiFetch<unknown>(`/customers/${customerId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return customerDetailSchema.parse(raw)
}

export async function createCustomerContact(
    customerId: string,
    input: ContactWriteInput,
): Promise<CustomerContact> {
    const body = toContactWriteRequest(input)
    const raw = await apiFetch<unknown>(`/customers/${customerId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
    })
    return customerContactSchema.parse(raw)
}

export async function updateCustomerContact(
    customerId: string,
    contactId: string,
    input: ContactWriteInput,
): Promise<CustomerContact> {
    const body = toContactWriteRequest(input)
    const raw = await apiFetch<unknown>(`/customers/${customerId}/contacts/${contactId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
    })
    return customerContactSchema.parse(raw)
}

export async function deleteCustomerContact(customerId: string, contactId: string): Promise<void> {
    await apiFetch<void>(`/customers/${customerId}/contacts/${contactId}`, {
        method: 'DELETE',
    })
}

export async function downloadCustomerImportTemplate(): Promise<void> {
    await downloadApiBlob('/customers/import/template', 'customers-import-template.csv')
}

export async function previewCustomerImport(file: File): Promise<CustomerImportPreview> {
    const formData = new FormData()
    formData.append('file', file)
    const raw = await apiFetch<unknown>('/customers/import/preview', {
        method: 'POST',
        body: formData,
    })
    return customerImportPreviewSchema.parse(raw)
}

export async function importCustomersCsv(file: File): Promise<CustomerImportResult> {
    const formData = new FormData()
    formData.append('file', file)
    const raw = await apiFetch<unknown>('/customers/import', {
        method: 'POST',
        body: formData,
    })
    return customerImportResultSchema.parse(raw)
}
